import useLocale from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  downloadSingleFile,
  MissingFileError,
} from '@/lib/mediaLibrary/zipDownload';

// Most images arrive before this; only slower files (videos) get a toast.
const PROGRESS_TOAST_DELAY_MS = 600;

/**
 * Download button for one image or video: straight from the CDN under a
 * readable name, with a progress toast when the file takes a while.
 * Resolves false when the download failed (already reported to the user).
 */
export const useMediaFileDownload = () => {
  const { t } = useLocale('ai-images');

  return useCallback(
    async (url: string, fileName: string): Promise<boolean> => {
      const toastId = `media-download-${url}`;
      let toastShown = false;
      let received = 0;
      let total: number | null = null;
      let shownPercent = -1;
      // Percent outside t(): an interpolated key would render raw on pages
      // that don't load this namespace.
      const label = () =>
        total
          ? `${t('Downloading…')} ${Math.round((received / total) * 100)}%`
          : t('Downloading…');
      const timer = setTimeout(() => {
        toastShown = true;
        toast.loading(label(), { id: toastId });
      }, PROGRESS_TOAST_DELAY_MS);

      try {
        await downloadSingleFile({
          url,
          fileName,
          onProgress: (bytes, size) => {
            received = bytes;
            total = size;
            if (!toastShown || !size) return;
            const percent = Math.round((bytes / size) * 100);
            if (percent === shownPercent) return;
            shownPercent = percent;
            toast.loading(label(), { id: toastId });
          },
        });
        if (toastShown) toast.dismiss(toastId);
        return true;
      } catch (error) {
        toast.error(
          error instanceof MissingFileError
            ? t('This file is no longer available.')
            : t('Download failed. Please try again.'),
          toastShown ? { id: toastId } : undefined,
        );
        return false;
      } finally {
        clearTimeout(timer);
      }
    },
    [t],
  );
};
